<template>
  <div class="app-container dida-overview">
    <dida-module-header
      title="生产运营总览"
      description="集中巡检道旅资源发布、订单链路、回传告警、差异推送时效以及各供应商实时队列容量。"
      :status="overallStatus"
      :status-text="overallStatusText"
      :updated-at="lastRefreshedAt"
      auto-refresh-text="每 30 秒自动刷新"
      :loading="loading"
      @refresh="loadOverview"
    />

    <el-alert
      v-if="errors.length"
      class="overview-alert"
      :title="`有 ${errors.length} 项生产指标读取失败：${errors.join('；')}`"
      type="error"
      :closable="false"
      show-icon
    />
    <el-alert
      v-else-if="policyConflict"
      class="overview-alert"
      title="道旅出站执行策略不是 Worker Only，或检测到 QPS 限制已开启；当前状态不符合生产要求。"
      type="error"
      :closable="false"
      show-icon
    />
    <el-alert
      v-else-if="unclassifiedRatePlans > 0"
      class="overview-alert"
      :title="`仍有 ${unclassifiedRatePlans} 个有效 RatePlan 待分类；执行一次完整校准后会补齐协议价/活动价口径。`"
      type="warning"
      :closable="false"
      show-icon
    />

    <section class="metric-grid" aria-label="道旅生产核心指标">
      <router-link v-for="metric in metrics" :key="metric.key" :to="metric.path" class="metric-card" :class="`is-${metric.level}`">
        <div class="metric-card__top">
          <span>{{ metric.label }}</span>
          <i :class="metric.icon" />
        </div>
        <strong>{{ metric.value }}</strong>
        <small>{{ metric.description }}</small>
      </router-link>
    </section>

    <section class="supplier-section">
      <div class="section-heading">
        <div>
          <h2>供应商推送容量与积压</h2>
          <p>全量、近期、中远期和预订失败补推均为独立 Worker 通道；以下积压来自共享 Redis，不使用 QPS 换算。</p>
        </div>
        <el-tag :type="policyConflict ? 'danger' : 'success'" effect="dark">
          {{ policyConflict ? '执行策略冲突' : 'Worker Only · QPS 未启用' }}
        </el-tag>
      </div>

      <div class="supplier-grid">
        <article v-for="supplier in suppliers" :key="supplier.value" class="supplier-card">
          <header>
            <div>
              <h3>{{ supplier.label }}</h3>
              <span>推送窗口 {{ supplierConfig(supplier.value).push_days }} 天</span>
            </div>
            <el-tag size="mini" :type="supplierHealth(supplier.value).type">{{ supplierHealth(supplier.value).text }}</el-tag>
          </header>

          <div class="worker-strip">
            <div><span>全量</span><strong>{{ supplierConfig(supplier.value).worker_count }}</strong></div>
            <div><span>近期</span><strong>{{ supplierConfig(supplier.value).realtime_recent_workers }}</strong></div>
            <div><span>中远期</span><strong>{{ supplierConfig(supplier.value).realtime_mid_future_workers }}</strong></div>
            <div><span>紧急补推</span><strong>{{ supplierConfig(supplier.value).booking_recovery_workers }}</strong></div>
          </div>

          <div class="calibration-row">
            <div>
              <span class="lane-heading">全量校准 <el-tag size="mini" :type="fullStatusTagType(supplier.value)">{{ fullStatusText(fullLane(supplier.value).status) }}</el-tag></span>
              <small>{{ fullLane(supplier.value).processed_hotels }}/{{ fullLane(supplier.value).authorized_hotels }} 家 · 失败 {{ fullLane(supplier.value).failed }} · 重试 {{ fullLane(supplier.value).retry_hotels }}</small>
            </div>
            <strong>{{ fullLane(supplier.value).progress_percent }}%</strong>
          </div>
          <el-progress :percentage="Number(fullLane(supplier.value).progress_percent || 0)" :status="fullProgressStatus(supplier.value)" :show-text="false" />
          <el-divider class="supplier-lane-divider" />

          <div class="lane-row">
            <div class="lane-copy"><span class="lane-heading">近期积压 <el-tag size="mini" :type="laneStatusTagType(supplier.value, 'recent')">{{ laneStatusText(supplier.value, 'recent') }}</el-tag></span><small>每 Worker {{ perWorker(supplier.value, 'recent') }}</small></div>
            <strong>{{ lane(supplier.value, 'recent').ready }}</strong>
          </div>
          <el-progress :percentage="lanePercentage(supplier.value, 'recent')" :status="laneProgressStatus(supplier.value, 'recent')" :show-text="false" />
          <div class="lane-detail">
            <span>处理中 {{ lane(supplier.value, 'recent').processing }}</span>
            <span>重试 {{ lane(supplier.value, 'recent').delayed_retry }}</span>
            <span>实际 Worker {{ lane(supplier.value, 'recent').actual_workers }}</span>
            <span>运行进程 {{ lane(supplier.value, 'recent').worker_processes }} / 主数据阻塞 {{ lane(supplier.value, 'recent').blocked_master }}</span>
            <span>最早等待 {{ formatWait(lane(supplier.value, 'recent').oldest_wait_seconds) }}</span>
          </div>
          <el-divider class="supplier-lane-divider" />

          <div class="lane-row">
            <div class="lane-copy"><span class="lane-heading">中远期积压 <el-tag size="mini" :type="laneStatusTagType(supplier.value, 'future')">{{ laneStatusText(supplier.value, 'future') }}</el-tag></span><small>每 Worker {{ perWorker(supplier.value, 'future') }}</small></div>
            <strong>{{ lane(supplier.value, 'future').ready }}</strong>
          </div>
          <el-progress :percentage="lanePercentage(supplier.value, 'future')" :status="laneProgressStatus(supplier.value, 'future')" :show-text="false" />
          <div class="lane-detail">
            <span>处理中 {{ lane(supplier.value, 'future').processing }}</span>
            <span>重试 {{ lane(supplier.value, 'future').delayed_retry }}</span>
            <span>实际 Worker {{ lane(supplier.value, 'future').actual_workers }}</span>
            <span>运行进程 {{ lane(supplier.value, 'future').worker_processes }} / 主数据阻塞 {{ lane(supplier.value, 'future').blocked_master }}</span>
            <span>最早等待 {{ formatWait(lane(supplier.value, 'future').oldest_wait_seconds) }}</span>
          </div>
          <el-divider class="supplier-lane-divider" />

          <div class="lane-row">
            <div class="lane-copy"><span class="lane-heading">预订失败紧急补推 <el-tag size="mini" type="danger">最高优先级</el-tag> <el-tag size="mini" :type="laneStatusTagType(supplier.value, 'booking_recovery')">{{ laneStatusText(supplier.value, 'booking_recovery') }}</el-tag></span><small>每 Worker {{ perWorker(supplier.value, 'booking_recovery') }}</small></div>
            <strong>{{ lane(supplier.value, 'booking_recovery').ready }}</strong>
          </div>
          <el-progress :percentage="lanePercentage(supplier.value, 'booking_recovery')" :status="laneProgressStatus(supplier.value, 'booking_recovery')" :show-text="false" />
          <div class="lane-detail">
            <span>处理中 {{ lane(supplier.value, 'booking_recovery').processing }}</span>
            <span>重试 {{ lane(supplier.value, 'booking_recovery').delayed_retry }}</span>
            <span>进程 {{ lane(supplier.value, 'booking_recovery').worker_processes }}</span>
            <span>最早等待 {{ formatWait(lane(supplier.value, 'booking_recovery').oldest_wait_seconds) }}</span>
          </div>
        </article>
      </div>
    </section>

    <section class="runbook-section">
      <div class="section-heading">
        <div>
          <h2>生产巡检入口</h2>
          <p>从告警定位到请求证据和资源修复，避免在多个菜单之间反复查找。</p>
        </div>
      </div>
      <div class="runbook-grid">
        <router-link v-for="item in runbooks" :key="item.path" :to="item.path" class="runbook-card">
          <i :class="item.icon" />
          <div><strong>{{ item.title }}</strong><span>{{ item.description }}</span></div>
          <i class="el-icon-arrow-right" />
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import request from '@/utils/request'
import DidaModuleHeader from './DidaModuleHeader.vue'

const emptyLane = () => ({ total: 0, ready: 0, processing: 0, delayed_retry: 0, oldest_wait_seconds: 0, actual_workers: 0, worker_processes: 0, blocked_master: 0, worker_heartbeat_stale: true })
const emptyFull = () => ({ status: 'idle', authorized_hotels: 0, processed_hotels: 0, progress_percent: 0, failed: 0, retry_hotels: 0, heartbeat_age_seconds: null })
const emptySupplier = () => ({ worker_count: 0, realtime_recent_workers: 0, realtime_mid_future_workers: 0, booking_recovery_workers: 0, push_days: 0 })

export default {
  name: 'DidaOverview',
  components: { DidaModuleHeader },
  data() {
    return {
      loading: false,
      refreshing: false,
      errors: [],
      lastRefreshedAt: '',
      refreshTimer: null,
      managedHotels: 0,
      abnormalOrders: 0,
      feedbackWarnings: 0,
      unresolvedIncidents: 0,
      ratePlanTotals: {
        protocol: { rate_plans: 0, ari_rates: 0 },
        activity: { rate_plans: 0, ari_rates: 0 },
        unclassified: { rate_plans: 0, ari_rates: 0 },
      },
      executionPolicy: { mode: 'worker_only', qps_limit_enabled: false },
      configBySupplier: {},
      backlogBySupplier: {},
      suppliers: [
        { value: 'jin_jiang', label: '锦江' },
        { value: 'sui_chuan', label: '隧穿亚朵' },
        { value: 'li_cheng', label: '丽呈' },
        { value: 'long_teng', label: '龙腾' },
      ],
      runbooks: [
        { path: '/dida/push-monitor', icon: 'el-icon-warning-outline', title: '处理推送告警', description: '查看订单回传和资源延迟的请求证据' },
        { path: '/dida/hotel-publish-controls', icon: 'el-icon-refresh', title: '诊断资源发布', description: '定位酒店开关、批次任务和最后成功快照' },
        { path: '/dida/activity-rates', icon: 'el-icon-price-tag', title: '核对活动价', description: '逐条确认活动价 RatePlan 已推送或被过滤' },
        { path: '/dida/order-monitor', icon: 'el-icon-connection', title: '核查订单链路', description: '核对订单、金额、额度和供应商状态' },
        { path: '/dida/worker-controls', icon: 'el-icon-cpu', title: '调整推送容量', description: '依据近期和中远期积压调整独立 Worker' },
      ],
    }
  },
  computed: {
    policyConflict() {
      return this.executionPolicy.mode !== 'worker_only' || this.executionPolicy.qps_limit_enabled === true
    },
    readyBacklog() {
      return this.sumLanes('ready')
    },
    delayedRetries() {
      return this.sumLanes('delayed_retry')
    },
    processingBacklog() {
      return this.sumLanes('processing')
    },
    actualWorkers() {
      return this.sumLanes('actual_workers')
    },
    workerProcesses() {
      return this.sumLanes('worker_processes')
    },
    blockedMaster() {
      return this.sumLanes('blocked_master')
    },
    oldestRealtimeWait() {
      return this.suppliers.reduce((oldest, item) => Math.max(oldest, Number(this.lane(item.value, 'recent').oldest_wait_seconds || 0), Number(this.lane(item.value, 'future').oldest_wait_seconds || 0), Number(this.lane(item.value, 'booking_recovery').oldest_wait_seconds || 0)), 0)
    },
    configuredWorkers() {
      return this.suppliers.reduce((total, item) => {
        const config = this.supplierConfig(item.value)
        return total + Number(config.worker_count || 0) + Number(config.realtime_recent_workers || 0) + Number(config.realtime_mid_future_workers || 0) + Number(config.booking_recovery_workers || 0)
      }, 0)
    },
    unclassifiedRatePlans() {
      return Number((this.ratePlanTotals.unclassified || {}).rate_plans || 0)
    },
    overallStatus() {
      if (this.errors.length || this.policyConflict || (this.readyBacklog > 0 && this.workerProcesses <= 0) || this.oldestRealtimeWait > 600) return 'danger'
      if (this.unresolvedIncidents > 0 || this.feedbackWarnings > 0 || this.abnormalOrders > 0 || this.readyBacklog > this.configuredWorkers || this.calibrationAttention || this.unclassifiedRatePlans > 0) return 'warning'
      return 'healthy'
    },
    overallStatusText() {
      if (this.errors.length) return '部分生产指标不可用'
      if (this.policyConflict) return '执行策略冲突'
      if (this.readyBacklog > 0 && this.workerProcesses <= 0) return '实时 Worker 无心跳'
      if (this.oldestRealtimeWait > 600) return '实时推送等待超过10分钟'
      if (this.overallStatus === 'warning') return '存在待处理项'
      return '生产链路正常'
    },
    metrics() {
      return [
        { key: 'hotels', label: '受控发布酒店', value: this.managedHotels, description: '道旅酒店发布控制总数', path: '/dida/hotel-publish-controls', icon: 'el-icon-office-building', level: 'normal' },
        { key: 'orders', label: '异常订单', value: this.abnormalOrders, description: '订单链路健康状态为异常', path: '/dida/order-monitor?health_status=ERROR', icon: 'el-icon-warning-outline', level: this.abnormalOrders ? 'danger' : 'success' },
        { key: 'feedback', label: '订单回传告警', value: this.feedbackWarnings, description: '待处理或重试的道旅回传', path: '/dida/push-monitor', icon: 'el-icon-chat-dot-round', level: this.feedbackWarnings ? 'warning' : 'success' },
        { key: 'incidents', label: '资源推送异常', value: this.unresolvedIncidents, description: '尚未恢复的时效异常', path: '/dida/push-monitor', icon: 'el-icon-alarm-clock', level: this.unresolvedIncidents ? 'danger' : 'success' },
        { key: 'ready', label: '可处理积压', value: this.readyBacklog, description: '近期、中远期与紧急补推合计', path: '/dida/worker-controls', icon: 'el-icon-s-data', level: this.readyBacklog > this.configuredWorkers ? 'warning' : 'success' },
        { key: 'processing', label: '正在处理', value: this.processingBacklog, description: '已领取且尚未确认的任务', path: '/dida/worker-controls', icon: 'el-icon-loading', level: 'normal' },
        { key: 'retry', label: '延迟重试', value: this.delayedRetries, description: '等待下一次执行的任务', path: '/dida/worker-controls', icon: 'el-icon-time', level: this.delayedRetries ? 'warning' : 'success' },
        { key: 'actual_workers', label: '实际 Worker', value: this.actualWorkers, description: '近期、中远期与紧急补推活跃 Worker 合计', path: '/dida/worker-controls', icon: 'el-icon-cpu', level: this.readyBacklog > 0 && this.actualWorkers <= 0 ? 'danger' : 'normal' },
        { key: 'blocked_master', label: '主数据阻塞', value: this.blockedMaster, description: '等待静态 RatePlan 生效后自动恢复', path: '/dida/worker-controls', icon: 'el-icon-lock', level: this.blockedMaster ? 'warning' : 'success' },
        { key: 'workers', label: '配置 Worker', value: this.configuredWorkers, description: '全量、近期、中远期、紧急补推合计', path: '/dida/worker-controls', icon: 'el-icon-cpu', level: this.policyConflict ? 'danger' : 'normal' },
        { key: 'protocol_rates', label: '协议价已推送', value: Number(this.ratePlanTotals.protocol.rate_plans || 0), description: 'Dida已接受且当前有效的 RatePlan', path: '/dida/hotel-publish-controls', icon: 'el-icon-tickets', level: 'success' },
        { key: 'activity_rates', label: '活动价已推送', value: Number(this.ratePlanTotals.activity.rate_plans || 0), description: 'Dida已接受且当前有效的 RatePlan', path: '/dida/activity-rates', icon: 'el-icon-price-tag', level: 'success' },
      ]
    },
    calibrationAttention() {
      return this.suppliers.some(item => ['warning', 'danger'].includes(this.fullLevel(item.value)))
    },
  },
  created() {
    this.loadOverview()
    this.refreshTimer = setInterval(() => {
      if (!document.hidden) this.loadOverview(false)
    }, 30000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    safeRequest(key, label, options) {
      return request(options).then(response => ({ key, response })).catch(() => ({ key, error: label }))
    },
    loadOverview(showLoading = true) {
      if (this.refreshing) return Promise.resolve()
      this.refreshing = true
      if (showLoading) this.loading = true
      this.errors = []
      const calls = [
        this.safeRequest('workers', 'Worker 配置', { url: '/api/backend/dida/worker-controls', method: 'get' }),
        this.safeRequest('backlog', '实时队列积压', { url: '/api/backend/dida/worker-controls/backlog', method: 'get' }),
        this.safeRequest('hotels', '酒店发布规模', { url: '/api/backend/dida/hotel-publish-controls', method: 'get', params: { page: 1, limit: 1 } }),
        this.safeRequest('orders', '异常订单', { url: '/api/backend/dida/order-monitor', method: 'get', params: { page: 1, limit: 1, health_status: 'ERROR' } }),
        this.safeRequest('feedback', '订单回传告警', { url: '/api/backend/dida/order-feedback/list', method: 'get', params: { page: 1, limit: 1, warning_only: 1 } }),
        this.safeRequest('incidents', '资源推送异常', { url: '/api/backend/dida/resource-push/incidents', method: 'get', params: { page: 1, limit: 1, unresolved_only: 1 } }),
        this.safeRequest('rates', '协议价/活动价推送统计', { url: '/api/backend/dida/rate-plan-overview', method: 'get' }),
      ]
      return Promise.all(calls).then(results => {
        results.forEach(result => {
          if (result.error) { this.errors.push(result.error); return }
          this.applyResult(result.key, result.response)
        })
        this.lastRefreshedAt = new Date().toLocaleString()
      }).finally(() => { this.loading = false; this.refreshing = false })
    },
    applyResult(key, response) {
      const data = response && response.data ? response.data : {}
      if (key === 'workers') {
        this.executionPolicy = data.execution_policy || this.executionPolicy
        const values = {}
        ;(data.suppliers || []).forEach(item => { values[item.scope] = item })
        this.configBySupplier = values
      } else if (key === 'backlog') {
        if (data.status === 'unavailable') this.errors.push(data.message || '实时队列积压')
        this.executionPolicy = data.execution_policy || this.executionPolicy
        const values = {}
        ;(data.suppliers || []).forEach(item => { values[item.scope] = item })
        this.backlogBySupplier = values
      } else if (key === 'hotels') this.managedHotels = this.responseTotal(data)
      else if (key === 'orders') this.abnormalOrders = this.responseTotal(data)
      else if (key === 'feedback') this.feedbackWarnings = this.responseTotal(data)
      else if (key === 'incidents') this.unresolvedIncidents = this.responseTotal(data)
      else if (key === 'rates') this.ratePlanTotals = data.totals || this.ratePlanTotals
    },
    responseTotal(data) {
      if (data.total !== undefined) return Number(data.total || 0)
      if (data.meta && data.meta.total !== undefined) return Number(data.meta.total || 0)
      return 0
    },
    supplierConfig(supplier) {
      return this.configBySupplier[supplier] || emptySupplier()
    },
    lane(supplier, segment) {
      const row = this.backlogBySupplier[supplier]
      return row && row[segment] ? row[segment] : emptyLane()
    },
    fullLane(supplier) {
      const row = this.backlogBySupplier[supplier]
      return row && row.full ? row.full : emptyFull()
    },
    laneWorkers(supplier, segment) {
      const metric = this.lane(supplier, segment)
      return Math.max(0, Number(metric.configured_workers || metric.actual_workers || 0))
    },
    perWorker(supplier, segment) {
      const ready = Number(this.lane(supplier, segment).ready || 0)
      const workers = this.laneWorkers(supplier, segment)
      return workers > 0 ? (ready / workers).toFixed(1) : ready.toFixed(1)
    },
    laneLevel(supplier, segment) {
      const metric = this.lane(supplier, segment)
      if (Number(metric.ready || 0) > 0 && (metric.worker_heartbeat_stale || Number(metric.worker_processes || 0) <= 0)) return 'danger'
      if (Number(metric.oldest_wait_seconds || 0) > 600) return 'danger'
      if (Number(metric.blocked_master || 0) > 0) return 'warning'
      const value = Number(this.perWorker(supplier, segment))
      if (value > 5) return 'danger'
      if (value > 1) return 'warning'
      return 'success'
    },
    lanePercentage(supplier, segment) {
      return Math.min(100, Math.round(Number(this.perWorker(supplier, segment)) * 10))
    },
    laneProgressStatus(supplier, segment) {
      const level = this.laneLevel(supplier, segment)
      return level === 'danger' ? 'exception' : level
    },
    laneStatusTagType(supplier, segment) {
      const level = this.laneLevel(supplier, segment)
      return level === 'danger' ? 'danger' : (level === 'warning' ? 'warning' : 'success')
    },
    laneStatusText(supplier, segment) {
      const level = this.laneLevel(supplier, segment)
      return level === 'danger' ? '异常' : (level === 'warning' ? '关注' : '顺畅')
    },
    supplierHealth(supplier) {
      const full = this.fullLevel(supplier)
      const recent = this.laneLevel(supplier, 'recent')
      const future = this.laneLevel(supplier, 'future')
      const recovery = this.laneLevel(supplier, 'booking_recovery')
      if (full === 'danger' || recent === 'danger' || future === 'danger' || recovery === 'danger') return { type: 'danger', text: '异常' }
      if (full === 'warning' || recent === 'warning' || future === 'warning' || recovery === 'warning') return { type: 'warning', text: '关注' }
      return { type: 'success', text: '正常' }
    },
    fullLevel(supplier) {
      const full = this.fullLane(supplier)
      if (['retry_requested', 'retrying_failures', 'ignore_requested'].includes(full.status)) return 'warning'
      if (full.status === 'completed_with_errors' || Number(full.retry_hotels || 0) > 0) return 'danger'
      if (full.status === 'running' && Number(full.heartbeat_age_seconds || 0) > 300) return 'danger'
      if (['idle', 'replay_requested'].includes(full.status) || Number(full.failed || 0) > 0) return 'warning'
      return 'success'
    },
    fullProgressStatus(supplier) {
      const level = this.fullLevel(supplier)
      return level === 'danger' ? 'exception' : level
    },
    fullStatusTagType(supplier) {
      const level = this.fullLevel(supplier)
      return level === 'danger' ? 'danger' : (level === 'warning' ? 'warning' : 'success')
    },
    fullStatusText(status) {
      return { idle: '无记录', running: '进行中', completed: '已完成', completed_with_errors: '有失败', completed_ignored: '异常已忽略', replay_requested: '待重跑', retry_requested: '等待重试', retrying_failures: '重试中', ignore_requested: '等待忽略' }[status] || status || '未知'
    },
    sumLanes(field) {
      return this.suppliers.reduce((total, item) => total + Number(this.lane(item.value, 'recent')[field] || 0) + Number(this.lane(item.value, 'future')[field] || 0) + Number(this.lane(item.value, 'booking_recovery')[field] || 0), 0)
    },
    formatWait(seconds) {
      const value = Number(seconds || 0)
      if (value < 60) return `${value} 秒`
      if (value < 3600) return `${Math.floor(value / 60)} 分钟`
      return `${Math.floor(value / 3600)} 小时 ${Math.floor((value % 3600) / 60)} 分`
    },
  },
}
</script>

<style lang="scss" scoped>
.overview-alert { margin-bottom: 14px; }
.metric-grid { display: grid; grid-template-columns: repeat(7, minmax(145px, 1fr)); gap: 12px; margin-bottom: 18px; }
.metric-card { display: flex; min-height: 126px; padding: 17px; color: #303133; text-decoration: none; border: 1px solid #e4e7ed; border-top: 4px solid #6f8fae; border-radius: 7px; background: #fff; box-shadow: 0 3px 12px rgba(31, 45, 61, .05); flex-direction: column; transition: transform .2s, box-shadow .2s; }
.metric-card:hover { transform: translateY(-2px); box-shadow: 0 7px 18px rgba(31, 45, 61, .1); }
.metric-card.is-success { border-top-color: #45a66b; }
.metric-card.is-warning { border-top-color: #e6a23c; }
.metric-card.is-danger { border-top-color: #f56c6c; }
.metric-card__top { display: flex; justify-content: space-between; color: #606266; font-size: 12px; }
.metric-card__top i { font-size: 18px; }
.metric-card strong { margin: 13px 0 7px; font-size: 29px; line-height: 34px; }
.metric-card small { color: #909399; line-height: 17px; }
.supplier-section, .runbook-section { margin-bottom: 18px; padding: 20px; border: 1px solid #e4e7ed; border-radius: 8px; background: #fff; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 16px; }
.section-heading h2 { margin: 0; color: #303133; font-size: 18px; }
.section-heading p { margin: 6px 0 0; color: #909399; font-size: 13px; }
.supplier-grid { display: grid; grid-template-columns: repeat(2, minmax(460px, 1fr)); gap: 16px; }
.supplier-card { min-width: 0; padding: 16px; border: 1px solid #dfe4ed; border-radius: 7px; background: #fbfcfe; box-shadow: 0 2px 8px rgba(31, 45, 61, .04); }
.supplier-card header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 14px; }
.supplier-card h3 { margin: 0 0 4px; color: #303133; font-size: 16px; }
.supplier-card header span { color: #909399; font-size: 12px; }
.worker-strip { display: grid; grid-template-columns: repeat(4, 1fr); margin-bottom: 16px; overflow: hidden; border: 1px solid #e4e7ed; border-radius: 5px; background: #fff; }
.worker-strip div { padding: 9px 5px; text-align: center; }
.worker-strip div + div { border-left: 1px solid #e4e7ed; }
.worker-strip span, .worker-strip strong { display: block; }
.worker-strip span { margin-bottom: 4px; color: #909399; font-size: 11px; }
.worker-strip strong { color: #1f5f93; font-size: 18px; }
.lane-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.calibration-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.calibration-row > div { display: flex; min-width: 0; flex-direction: column; }
.calibration-row span { color: #606266; font-size: 13px; }
.lane-heading { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.calibration-row small { overflow: hidden; color: #909399; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.calibration-row strong { color: #303133; font-size: 18px; }
.lane-row > strong { color: #303133; font-size: 21px; }
.lane-copy { display: flex; flex-direction: column; }
.lane-copy span { color: #606266; font-size: 13px; }
.lane-copy small, .lane-detail { color: #909399; font-size: 11px; }
.lane-detail { display: flex; justify-content: space-between; margin-top: 7px; }
.supplier-lane-divider { margin: 14px 0; background-color: #e4e7ed; }
.runbook-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.runbook-card { display: flex; align-items: center; gap: 12px; min-height: 76px; padding: 14px 16px; color: #303133; text-decoration: none; border: 1px solid #e4e7ed; border-radius: 6px; transition: border-color .2s, background .2s; }
.runbook-card:hover { border-color: #87b9de; background: #f4f9fe; }
.runbook-card > i:first-child { color: #2f86c7; font-size: 23px; }
.runbook-card > i:last-child { margin-left: auto; color: #c0c4cc; }
.runbook-card div { display: flex; min-width: 0; flex-direction: column; }
.runbook-card strong { margin-bottom: 5px; }
.runbook-card span { overflow: hidden; color: #909399; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 1500px) { .metric-grid { grid-template-columns: repeat(4, 1fr); } }
@media (max-width: 900px) { .metric-grid { grid-template-columns: repeat(2, 1fr); } .supplier-grid, .runbook-grid { grid-template-columns: 1fr; } .section-heading { align-items: flex-start; flex-direction: column; } }
@media (max-width: 520px) { .metric-grid { grid-template-columns: 1fr; } .supplier-section, .runbook-section { padding: 14px; } }
</style>
